import React, { useState } from 'react';
import { Share2Icon } from 'lucide-react';
import { ShareCardFrame } from '../../components/engagement/ShareCardFrame';
import { ShareSheet } from '../../components/engagement/ShareSheet';
import { PageHeader } from '../../components/PageHeader';
import { SegmentedTabs } from '../../components/SegmentedTabs';
import { Button } from '../../components/ui/Button';
import { Card, SectionTitle } from '../../components/ui/Card';
import { recaps } from '../../data/engagement';
import { useLocale } from '../../contexts/LocaleContext';
import type { Recap as RecapData } from '../../types/engagement';

/** MOB/Stats/Recap — monthly / yearly summary with a shareable final card. */
export function Recap() {
  const {
    t,
    tl
  } = useLocale();
  const [period, setPeriod] = useState<RecapData['period']>('month');
  const [sharing, setSharing] = useState(false);
  const recap = recaps.find(item => item.period === period) ?? recaps[0];
  const facts = [{
    label: t('eng.recap.streak'),
    value: t('eng.recap.streakValue', {
      n: recap.longestStreak
    })
  }, {
    label: t('eng.recap.checkDays'),
    value: String(recap.checkDays)
  }, {
    label: t('eng.recap.habit'),
    value: tl(recap.mostConsistentHabit)
  }, {
    label: t('eng.recap.activity'),
    value: tl(recap.mainActivity)
  }];
  return <div className="px-2 pb-2" data-id="element-2563" data-mp="21xlgtrb68uld">
      <PageHeader title={t('eng.recap.title')} subtitle={t('eng.recap.subtitle')} showBack data-id="element-2564" data-mp="21yn18me2utfr" />

      <SegmentedTabs label={t('eng.recap.title')} value={period} onChange={setPeriod} tabs={[{
      id: 'month',
      label: t('eng.recap.month')
    }, {
      id: 'year',
      label: t('eng.recap.year')
    }]} data-id="element-2565" data-mp="2zk29gj8ymk0s" />

      <Card tone="dark" className="mt-3 rounded-panel p-3" data-id="element-2566" data-mp="2kw7e0v3ki1k0">
        <h2 className="text-title text-sand" data-id="element-2567" data-mp="2kvo6m8bjmvk8">{tl(recap.title)}</h2>
        <p className="bee-overline mt-3 text-[9px] font-bold text-ondark" data-id="element-2568" data-mp="3ibv0w98drx6e">{t('eng.recap.avg')}</p>
        <p className="bee-num text-[56px] font-bold leading-none text-gold" data-id="element-2569" data-mp="3dkldlfab15xo">{recap.averageLifeScore}</p>

        <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-sand/15 pt-3" data-id="element-2570" data-mp="1bn2wdkd8xz2m">
          {facts.map(fact => <div key={fact.label} data-id="element-2571" data-mp="1foejnj8ne87n">
              <dt className="text-[11px] text-ondark" data-id="element-2572" data-mp="11xj8u1el9h1f">{fact.label}</dt>
              <dd className="bee-num text-[15px] font-bold text-sand" data-id="element-2573" data-mp="11x9kei96zhxu">{fact.value}</dd>
            </div>)}
        </dl>
        <div className="mt-2 border-t border-sand/15 pt-2" data-id="element-2574" data-mp="rzbw5urc1l58">
          <p className="text-[11px] text-ondark" data-id="element-2575" data-mp="rxshg8lbri5k">{t('eng.recap.community')}</p>
          <p className="bee-num text-[14px] font-bold text-sand" data-id="element-2576" data-mp="wzhutuw0s7wt">
            {t('eng.recap.communityValue', {
            posts: recap.communityPosts,
            challenges: recap.challengesDone
          })}
          </p>
        </div>
      </Card>

      <section className="mt-3" data-id="element-2577" data-mp="2dcoos73qdc2z">
        <SectionTitle data-id="element-2578" data-mp="2ddq1sh4b48nc">{t('eng.recap.highlights')}</SectionTitle>
        <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-2579" data-mp="2ibri4uacltv4">
          {recap.highlights.map(item => <li key={tl(item.label)} className="flex items-baseline justify-between gap-3 border-b border-sand-line py-2 last:border-b-0" data-id="element-2580" data-mp="2sewxpxtlclck">
              <span className="text-[12px] text-muted" data-id="element-2581" data-mp="317rue34zt8qq">{tl(item.label)}</span>
              <span className="text-right text-[13px] font-medium text-deep" data-id="element-2582" data-mp="319uu5evy6g8n">{tl(item.value)}</span>
            </li>)}
        </ul>
      </section>

      <section className="mt-3" data-id="element-2583" data-mp="3b4wg61uxf270">
        <SectionTitle data-id="element-2584" data-mp="3b8ajbcbezffi">{t('eng.recap.card')}</SectionTitle>
        <ShareCardFrame label={t('eng.recap.card')} data-id="element-2585" data-mp="3661qcfyjkdde">
          <p className="text-[18px] font-bold leading-tight text-sand" data-id="element-2586" data-mp="3k6vyqyllaec2">{tl(recap.title)}</p>
          <p className="bee-overline mt-3 text-[8.5px] font-bold text-ondark" data-id="element-2587" data-mp="3k4jicpvhcc14">{t('eng.recap.avg')}</p>
          <p className="bee-num text-[48px] font-bold leading-none text-gold" data-id="element-2588" data-mp="3g3g6eegjz056">{recap.averageLifeScore}</p>
          <ul className="mt-3 space-y-1 text-[11.5px]" data-id="element-2589" data-mp="1lr9icf2k8ovg">
            <li className="flex justify-between gap-2" data-id="element-2590" data-mp="1lsuaxz5bdxqo">
              <span className="text-ondark" data-id="element-2591" data-mp="1n8ydd58emzpi">{t('eng.recap.streak')}</span>
              <span className="bee-num font-bold" data-id="element-2592" data-mp="1nark0kj0pjxi">{t('eng.recap.streakValue', {
                n: recap.longestStreak
              })}</span>
            </li>
            <li className="flex justify-between gap-2" data-id="element-2593" data-mp="1w8lia10cennb">
              <span className="text-ondark" data-id="element-2594" data-mp="1w70l03mn8qlr">{t('eng.recap.activity')}</span>
              <span className="font-bold" data-id="element-2595" data-mp="217oagkv84zdv">{tl(recap.mainActivity)}</span>
            </li>
            <li className="flex justify-between gap-2" data-id="element-2596" data-mp="14j8k4mkailzp">
              <span className="text-ondark" data-id="element-2597" data-mp="18jis24rn7mh7">{t('eng.recap.habit')}</span>
              <span className="font-bold" data-id="element-2598" data-mp="18hg21kqhptch">{tl(recap.mostConsistentHabit)}</span>
            </li>
          </ul>
        </ShareCardFrame>
        <Button variant="primary" size="lg" fullWidth className="mt-3" onClick={() => setSharing(true)} data-id="element-2599" data-mp="1i92bbbj9picl">
          <Share2Icon size={16} aria-hidden="true" data-id="element-2600" data-mp="kuhpgy5hr5ju" />
          {t('eng.recap.share')}
        </Button>
      </section>

      <ShareSheet open={sharing} onClose={() => setSharing(false)} data-id="element-2601" data-mp="prznhthz00d2" />
    </div>;
}